import type React from 'react'
import { PORT_HIT_SIZE, type PortPoint } from '@/lib/text-linking/text-linking-geometry'

import { PLUS_SIZE } from '../constants/port-style'
import { portControlAttrs } from '../handlers/port-control-attrs'
import type { PortControlProps } from '../types/port-control'
import { PortBadgeContainer } from './port-badge-container'
import { PortFocusRing } from './port-focus-ring'
import { PortSeqNumeral } from './port-seq-numeral'

const UNLINK_COLOR = '#EF4444'

interface LinkedOutPortSquareProps {
  center: PortPoint
  rotationDeg: number
  /**
   * Chain sequence number to show centered in the square. Only a chain *head* gets one (so in
   * practice always 1) — a follower's number lives at its in-port instead and this is `undefined`,
   * leaving the square as a plain unlink affordance. See `FrameOutPort` for the design rule.
   */
  seq?: number
  /** Hovered → shows the red unlink "×" affordance in place of the resting solid square. */
  hovered: boolean
  onClick: () => void
  onHoverChange: (hovered: boolean) => void
  control?: PortControlProps
}

/**
 * The linked out-port — a solid square at rest, red stroke + "×" cross on hover (click = unlink).
 * Sized on the same terms as `PortSquare`: it is the same numbered-square visual, so the two must
 * agree in on-screen size or a linked frame shows mismatched ports on its two edges — both are
 * screen pixels now, with no counter-scale for either to drift out of step on.
 */
export function LinkedOutPortSquare({
  center,
  rotationDeg,
  seq,
  hovered,
  onClick,
  onHoverChange,
  control,
}: LinkedOutPortSquareProps): React.JSX.Element {
  return (
    <g
      transform={`translate(${center.x}, ${center.y}) rotate(${rotationDeg})`}
      aria-hidden={control ? undefined : true}
    >
      <rect
        ref={control?.registerRef}
        x={-PORT_HIT_SIZE / 2}
        y={-PORT_HIT_SIZE / 2}
        width={PORT_HIT_SIZE}
        height={PORT_HIT_SIZE}
        fill="transparent"
        data-port-role="out"
        cursor={control?.disabled ? 'not-allowed' : 'pointer'}
        onClick={control?.disabled ? undefined : onClick}
        onMouseEnter={control?.disabled ? undefined : () => onHoverChange(true)}
        onMouseLeave={control?.disabled ? undefined : () => onHoverChange(false)}
        {...portControlAttrs(control)}
      />
      <PortBadgeContainer />
      {hovered ? (
        <g aria-hidden="true">
          <line
            x1={-PLUS_SIZE / 3}
            y1={-PLUS_SIZE / 3}
            x2={PLUS_SIZE / 3}
            y2={PLUS_SIZE / 3}
            stroke={UNLINK_COLOR}
            strokeWidth={1.5}
            pointerEvents="none"
          />
          <line
            x1={PLUS_SIZE / 3}
            y1={-PLUS_SIZE / 3}
            x2={-PLUS_SIZE / 3}
            y2={PLUS_SIZE / 3}
            stroke={UNLINK_COLOR}
            strokeWidth={1.5}
            pointerEvents="none"
          />
        </g>
      ) : (
        seq != null && (
          // Counter-rotate so the number stays upright even though the square above rotates
          // with the frame — matches PortSquare's in-port numeral treatment.
          <g transform={`rotate(${-rotationDeg})`} aria-hidden="true">
            <PortSeqNumeral seq={seq} />
          </g>
        )
      )}
      <PortFocusRing visible={control?.focused ?? false} />
    </g>
  )
}
